import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import CloseLine from '@/assets/icons/close_line.svg?component';
import Bioscope from '@/assets/icons/operational-dashboard/bioscope.svg?component';
import Done from '@/assets/icons/operational-dashboard/done.svg?component';
import Warning from '@/assets/icons/warning.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { TextLink } from '@/components/atoms/TextLink';
import { Menu, MenuContent, MenuDivider, MenuItem, MenuTrigger } from '@/components/molecules/Menu';
import type { Task, TaskPriority } from '@/domain/operationalDashboard';
import {
    renderTaskDescription,
    renderTaskGuideUrl,
    renderTaskTitle,
} from '@/domain/operationalDashboard';
import { formatDateDisplay } from '@/utils/date';

import IgnoreTaskModal from '../IgnoreTaskModal/IgnoreTaskModal';

import TaskPriorityIcon from './TaskPriorityIcon';
import TaskStatusBadge from './TaskStatusBadge';

import styles from './TaskCard.module.scss';

interface TaskCardProps {
    task: Task;
    onComplete: (taskId: number) => void;
    onIgnore: (params: {
        taskId: number;
        ignoredUntil?: Date;
        ignoredPermanently?: boolean;
    }) => void;
    onUnignore?: (taskId: number) => void;
}

const PRIORITY_BORDER_CLASS: Record<TaskPriority, string> = {
    critical: styles.cardBorderCritical,
    high: styles.cardBorderHigh,
    warning: styles.cardBorderWarning,
    low: styles.cardBorderLow,
};

export default function TaskCard(props: TaskCardProps) {
    const { task, onComplete, onIgnore, onUnignore } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clientId } = useParams<{ clientId: string }>();
    const [ignoreModalOpen, setIgnoreModalOpen] = useState(false);

    const cageBasePath = task.cageId
        ? `/c/${clientId}/location/${task.locationId}/pen/${task.cageId}`
        : null;

    const isActive = task.status === 'active';
    const isIgnored = task.status === 'ignored';
    const renderedTitle = renderTaskTitle(task, t);
    const renderedDescription = renderTaskDescription(task, t);
    const guideUrl = renderTaskGuideUrl(task, t);

    return (
        <Card
            radius="sm"
            accent="border"
            className={classNames(styles.card, PRIORITY_BORDER_CLASS[task.priority])}>
            <div className={styles.cardHeader}>
                <TaskPriorityIcon priority={task.priority} alertCode={task.alertCode} />
                <div className={styles.cardBody}>
                    <div className={styles.titleRow}>
                        <div className={styles.title}>{renderedTitle}</div>
                        <TaskStatusBadge status={task.status} priority={task.priority} />
                    </div>
                    {renderedDescription && (
                        <div className={styles.description}>
                            {renderedDescription}
                            {guideUrl && (
                                <>
                                    {' '}
                                    <a
                                        href={guideUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.guideLink}>
                                        {t('operationalDashboard.task.findGuideHere')}
                                    </a>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {task.recurrenceCount >= 2 && (
                // Not announced: it is part of the card as it loads, in every card that has it.
                <Alert
                    tone="danger"
                    size="small"
                    live="off"
                    icon={<SvgIcon icon={Warning} size="small" color="currentColor" />}>
                    {t('operationalDashboard.task.recurrenceWarning', {
                        count: task.recurrenceCount,
                    })}
                </Alert>
            )}
            <div className={styles.separator} />
            <div className={styles.footer}>
                {task.bioscopeId &&
                    (cageBasePath ? (
                        <TextLink
                            tone="quiet"
                            to={`${cageBasePath}/dashboard/bioscope`}
                            className={styles.metaItem}>
                            <SvgIcon
                                icon={Bioscope}
                                size="custom"
                                customSize={20}
                                color="var(--color-text-secondary)"
                            />
                            {task.bioscopeId}
                        </TextLink>
                    ) : (
                        <span className={styles.metaItem}>
                            <SvgIcon
                                icon={Bioscope}
                                size="custom"
                                customSize={20}
                                color="var(--color-text-secondary)"
                            />
                            {task.bioscopeId}
                        </span>
                    ))}
                {task.bioscopeId && task.cageName && (
                    <span className={styles.metaSeparator}>&middot;</span>
                )}
                {task.cageName &&
                    (cageBasePath ? (
                        <TextLink
                            tone="quiet"
                            to={`${cageBasePath}/dashboard`}
                            className={styles.metaItem}>
                            {task.cageName}
                        </TextLink>
                    ) : (
                        <span className={styles.metaItem}>{task.cageName}</span>
                    ))}
                {isIgnored && (task.ignoredPermanently || task.ignoredUntil) && (
                    <span className={styles.metaItem}>
                        {task.ignoredPermanently
                            ? t('operationalDashboard.task.ignoredPermanently')
                            : task.ignoredUntil
                              ? t('operationalDashboard.task.ignoredUntil', {
                                    date: formatDateDisplay(task.ignoredUntil),
                                })
                              : null}
                    </span>
                )}
                <div className={styles.footerActions}>
                    {(isActive || isIgnored) && (
                        <div className={styles.actionsDropdown}>
                            <Menu>
                                <MenuTrigger variant="secondary" caret>
                                    {t('operationalDashboard.task.actions')}
                                </MenuTrigger>
                                <MenuContent align="end" width="wide">
                                    {cageBasePath && (
                                        <MenuItem
                                            onClick={() =>
                                                navigate(`${cageBasePath}/dashboard/bioscope`)
                                            }>
                                            {t('operationalDashboard.task.viewBioscopeStatus')}
                                        </MenuItem>
                                    )}
                                    {cageBasePath && (
                                        <MenuItem
                                            onClick={() =>
                                                navigate(`${cageBasePath}/dashboard/images`)
                                            }>
                                            {t('operationalDashboard.task.viewTodaysCatch')}
                                        </MenuItem>
                                    )}
                                    <MenuItem
                                        onClick={() =>
                                            navigate(
                                                `/c/${clientId}/location/${task.locationId}/video`
                                            )
                                        }>
                                        {t('operationalDashboard.task.viewLiveVideo')}
                                    </MenuItem>
                                    {isActive && (
                                        <>
                                            <MenuDivider />
                                            <MenuItem
                                                icon={
                                                    <SvgIcon
                                                        icon={CloseLine}
                                                        size="custom"
                                                        customSize={12}
                                                        color="currentColor"
                                                    />
                                                }
                                                onClick={() => setIgnoreModalOpen(true)}>
                                                {t('operationalDashboard.task.ignoreTask')}
                                            </MenuItem>
                                        </>
                                    )}
                                </MenuContent>
                            </Menu>
                        </div>
                    )}
                    {isActive && (
                        <Button
                            variant="success"
                            type="button"
                            icon={<SvgIcon icon={Done} />}
                            onClick={() => onComplete(task.id)}>
                            {t('operationalDashboard.task.markDone')}
                        </Button>
                    )}
                    {isIgnored && onUnignore && (
                        <Button variant="success" type="button" onClick={() => onUnignore(task.id)}>
                            {t('operationalDashboard.task.unignoreTask')}
                        </Button>
                    )}
                </div>
            </div>
            <IgnoreTaskModal
                isOpen={ignoreModalOpen}
                task={task}
                onClose={() => setIgnoreModalOpen(false)}
                onConfirm={onIgnore}
            />
        </Card>
    );
}
